/**
 * Single-machine fallback `SessionController` (BUILD-SPEC.md §5 "Fallback").
 *
 * This is the permanent degrade path when Live Share is unavailable — not
 * throwaway code (see the task brief). It implements the exact same
 * `SessionController` interface as `src/live/session.ts`, over one local
 * machine's clock instead of the Live Share timestamp provider, with
 * `shared: false` so the UI can show the "not shared — others cannot see
 * this clock" banner (detected once at join, surfaced once, per BUILD-SPEC
 * §5).
 *
 * This is the ONE place in the app allowed to call `Date.now()` — see the
 * ESLint override in `.eslintrc.cjs` for `src/local/**`. Everywhere else,
 * `now` must come from the Live Share clock (BUILD-SPEC hard rule 3).
 *
 * There is no shared LiveTimer here to derive `zeroPointAt` from a
 * `finished` event (see BUILD-SPEC §5 / src/live/timer.ts for why that
 * matters over a network). On a single machine there's no clock-drift or
 * write-storm problem to guard against, so `zeroPointAt` is simply computed
 * directly, up front, at the moment the turn's zero-crossing is knowable —
 * `now + remainingFor(state, idx, now)` — which is the same value the
 * engine's own maths would derive at the true zero-crossing instant, just
 * computed eagerly rather than waited for.
 */
import type { QueueEntry, Session, Settings } from "../engine";
import {
  addInvited as engineAddInvited,
  addManual as engineAddManual,
  end as engineEnd,
  extend as engineExtend,
  next as engineNext,
  pause as enginePause,
  reset as engineReset,
  remainingFor,
  resume as engineResume,
  start as engineStart,
} from "../engine";
import type { AppOpenUser, SessionController } from "../contract";

function buildInitialSession(settings: Settings): Session {
  return {
    phase: "ticklist",
    queue: [],
    idx: 0,
    limitSeconds: settings.secondsPerPerson,
    graceSeconds: settings.graceSeconds,
    turnStartedAt: null,
    zeroPointAt: null,
    lastActionBy: "",
    lastActionAt: 0,
  };
}

/** With `zeroPointAt` computed eagerly (see file doc), running turns always
 * carry the correct zero-crossing for the *current* state of the entry —
 * recomputed here after any mutation that could move it (hand-off, resume,
 * extend). Pausing/banking doesn't need this: `zeroPointAt` is only ever
 * read relative to a live turn (`turnStartedAt !== null`), and is reset to
 * `null` by the engine itself whenever a turn stops being live. */
function withZeroPoint(state: Session, now: number): Session {
  if (state.phase !== "running" || state.turnStartedAt === null) {
    // Not a live turn: zeroPointAt means nothing here. The engine's own
    // transitions (start/next/end) already null it out, but pause()/
    // extend() only spread the previous Session, so a stale non-null value
    // can otherwise survive into a paused/finished state. Clear it
    // explicitly rather than trust every engine transition to have done so.
    return state.zeroPointAt === null ? state : { ...state, zeroPointAt: null };
  }
  const zeroPointAt = now + remainingFor(state, state.idx, now);
  return { ...state, zeroPointAt };
}

export interface LocalControllerIdentity {
  displayName: string;
  userId: string;
}

export interface LocalControllerOptions {
  settings: Settings;
  identity: LocalControllerIdentity;
}

/**
 * `clock` defaults to `Date.now` (the one legitimate use in this codebase)
 * but is overridable so this class stays unit-testable with a fake clock,
 * exactly like the pure engine it wraps.
 */
export class LocalSessionController implements SessionController {
  readonly shared = false;

  private cached: Session;
  private readonly listeners = new Set<() => void>();
  private disposed = false;

  constructor(
    settings: Settings,
    private readonly identity: LocalControllerIdentity,
    // Seconds, not milliseconds — `src/engine` adds `now - turnStartedAt`
    // directly onto `spentSeconds` (documented in seconds), with no
    // conversion of its own. `Date.now()` itself returns milliseconds, so
    // the default clock divides by 1000 right here. Getting this wrong
    // doesn't fail loudly: it makes overtime explode almost instantly,
    // because every real millisecond is miscounted as a full elapsed
    // second. (A test-injected clock is expected to already return
    // seconds, matching every test in this file — this default is only
    // for the real, production clock.)
    private readonly clock: () => number = () => Date.now() / 1000
  ) {
    this.cached = buildInitialSession(settings);
  }

  get session(): Session {
    return this.cached;
  }

  now(): number {
    return this.clock();
  }

  canControl(): boolean {
    return true; // BUILD-SPEC hard rule 5: no role gating, even locally.
  }

  appOpenUsers(): AppOpenUser[] {
    // Single machine: no presence to share, but "myself" is trivially open.
    return [{ userId: this.identity.userId, displayName: this.identity.displayName }];
  }

  private notify(): void {
    this.listeners.forEach((l) => l());
  }

  private commit(next: Session, now: number): void {
    if (next === this.cached) return; // dropped stale/no-op call.
    this.cached = withZeroPoint(next, now);
    this.notify();
  }

  start(order: QueueEntry[], settings: Settings): void {
    const now = this.clock();
    this.commit(
      engineStart(this.cached, { order, settings, now, by: this.identity.displayName }),
      now
    );
  }

  next(expectedIdx: number): void {
    const now = this.clock();
    this.commit(
      engineNext(this.cached, { expectedIdx, now, by: this.identity.displayName }),
      now
    );
  }

  pause(): void {
    const now = this.clock();
    this.commit(enginePause(this.cached, { now, by: this.identity.displayName }), now);
  }

  resume(): void {
    const now = this.clock();
    this.commit(engineResume(this.cached, { now, by: this.identity.displayName }), now);
  }

  extend(seconds: number): void {
    const now = this.clock();
    this.commit(
      engineExtend(this.cached, { seconds, now, by: this.identity.displayName }),
      now
    );
  }

  addManual(displayName: string): void {
    const now = this.clock();
    this.commit(
      engineAddManual(this.cached, { displayName, now, by: this.identity.displayName }),
      now
    );
  }

  addInvited(userId: string, displayName: string): void {
    const now = this.clock();
    this.commit(
      engineAddInvited(this.cached, {
        userId,
        displayName,
        now,
        by: this.identity.displayName,
      }),
      now
    );
  }

  end(): void {
    const now = this.clock();
    this.commit(engineEnd(this.cached, { now, by: this.identity.displayName }), now);
  }

  newStandup(): void {
    const now = this.clock();
    this.commit(engineReset(this.cached, { now, by: this.identity.displayName }), now);
  }

  resumeFromFinished(): void {
    // Mirrors src/live/session.ts's resumeFromFinished (see its comment):
    // the engine has no dedicated transition for "un-end" a standup, so this
    // restores phase: "running" for the current speaker with a fresh
    // turnStartedAt, leaving their banked spentSeconds untouched.
    const state = this.cached;
    if (state.phase !== "finished" || state.queue.length === 0) return;
    const now = this.clock();
    const idx = Math.min(state.idx, state.queue.length - 1);
    const queue = state.queue.map((entry, i) =>
      i === idx ? { ...entry, status: "speaking" as const } : entry
    );
    const next: Session = {
      ...state,
      phase: "running",
      queue,
      idx,
      turnStartedAt: now,
      zeroPointAt: null,
      lastActionBy: this.identity.displayName,
      lastActionAt: now,
    };
    this.commit(next, now);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.listeners.clear();
  }
}

/** Builds the local fallback controller. Used directly by
 * `src/live/session.ts`'s `createControllerFactory` when `joinContainer`
 * throws, and usable standalone (e.g. for a "run without Teams" dev mode). */
export function createLocalController(options: LocalControllerOptions): SessionController {
  return new LocalSessionController(options.settings, options.identity);
}
